import type { FloorItem, FloorRoom, Order } from "../../api/types";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";

/** The logical plane of every room (as in the prototype); positions are shown in percentages. */
const WIDTH = 1000;
const HEIGHT = 640;

const position = (item: FloorItem) => ({
  left: `${String((item.x / WIDTH) * 100)}%`,
  top: `${String((item.y / HEIGHT) * 100)}%`,
  width: `${String((item.w / WIDTH) * 100)}%`,
  height: `${String((item.h / HEIGHT) * 100)}%`,
});

interface Props {
  room: FloorRoom;
  openTables: ReadonlyMap<string, Order>;
  /** The table on the ticket now, highlighted. */
  current: string;
  now: number;
  /** Whether a table can be tapped (e.g. only occupied ones when choosing which table to move). */
  enabled: (table: string, occupied: boolean) => boolean;
  onTable: (table: string) => void;
  /** The Tafo app: tables with lines not sent to the kitchen yet (dashed, "niet verstuurd"). */
  withDraft?: ReadonlySet<string>;
}

/**
 * One room of the floor plan: tables where they stand in the shop, occupied ones orange with the
 * time at the table and the amount still open, free ones with only their number; bar, walls and text
 * as decoration.
 */
export function FloorPlan({ room, openTables, current, now, enabled, onTable, withDraft }: Props) {
  return (
    <div className="fp" role="group" aria-label={room.name}>
      {room.items.map((item) => {
        if (item.kind !== "table" || item.label === null) {
          return (
            <div key={item.id} className={`fpd ${item.kind}`} style={position(item)} aria-hidden="true">
              {item.text}
            </div>
          );
        }
        const table = item.label;
        const order = openTables.get(table);
        const minutes = order ? Math.max(0, Math.round((now - Date.parse(order.createdAt)) / 60_000)) : 0;
        const draft = withDraft?.has(table) === true;
        return (
          <button
            key={item.id}
            type="button"
            className={`fpt ${item.shape} ${order ? "busy" : ""} ${draft ? "draft" : ""} ${table === current ? "cur" : ""}`}
            style={position(item)}
            disabled={!enabled(table, order !== undefined)}
            aria-label={t.tables.label(table)}
            onClick={() => {
              onTable(table);
            }}
          >
            <b>{table}</b>
            {/* a free table: only its number (Furkan, 10/10/2026: "vrij · 4p moet er niet bij"); on a
                small plan an occupied one keeps only the amount: see .fp in till.css */}
            {order ? (
              <small>
                <span className="fp-more">{t.tables.minutes(minutes)} · </span>
                <span>{formatEuro(order.totalCents - order.paidCents)}</span>
              </small>
            ) : (
              draft && <small>{t.tafoApp.waiter.notSent}</small>
            )}
          </button>
        );
      })}
    </div>
  );
}
